// One custom field's drawer, opened from its tile and shown below all the tiles: the Exclude toggle, the
// Applied list with one row per value and a × that removes that value only, then the field's name with its
// type and the type's control. Its outline is the one the panel's open dropdown menus have.
import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import type {CustomFieldColumnDef} from '../custom-field-columns/customFieldColumnModel';
import SwitchLine from '../dropdowns/skill-dropdown/SwitchLine';
import {CrossIconOld} from '../../../../web-components/icons/cross-icon';
import type {ValueCondition} from './customFieldFilterConditions';
import {
	NOT_MESSAGE_ID,
	customFieldTypeLabel,
	customFieldValueLabel,
	type CustomFieldFilterFormat,
	type FormatMessage,
} from './customFieldFilterLabels';
import {Muted, NEGATIVE_TEXT, PANEL_ACCENT, PANEL_DIVIDER, PANEL_FONT_SIZE, PANEL_TEXT} from './customFieldFilter.styled';
import {CustomFieldConditionFilter} from './CustomFieldConditionFilter';
import {CustomFieldDropdownOptions} from './CustomFieldDropdownOptions';

const Drawer = Styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 14px;
	border: 1px solid ${PANEL_ACCENT};
	border-radius: 4px;
	background-color: #fff;
	font-size: ${PANEL_FONT_SIZE};
	color: ${PANEL_TEXT};
`;

// The panel's own Exclude line, given the drawer's row spacing and divider.
const ExcludeRow = Styled.div`
	cursor: pointer;

	&& > div {
		margin: 0;
		padding: 8px 12px;
		border-bottom: 1px solid ${PANEL_DIVIDER};
	}
`;

const Heading = Styled.div`
	padding: 10px 12px;
	border-bottom: 1px solid ${PANEL_DIVIDER};
	font-weight: 600;
	line-height: 1;
`;

const FieldHeading = Styled(Heading)`
	border-top: 1px solid ${PANEL_DIVIDER};
`;

const AppliedRows = Styled.div`
	&:not(:empty) {
		padding: 4px 0;
	}
`;

const AppliedRow = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 6px 12px;
	line-height: 1.3;
`;

const NotWord = Styled.span`
	color: ${NEGATIVE_TEXT};
`;

const RemoveButton = Styled.button`
	display: flex;
	align-items: center;
	padding: 0 4px;
	border: none;
	background: none;
	cursor: pointer;
`;

export interface CustomFieldFilterDrawerProps {
	definition: CustomFieldColumnDef;
	values: unknown[];
	excluded: boolean;
	allowExclude: boolean;
	format: CustomFieldFilterFormat;
	onReplace: (values: unknown[]) => void;
	onExcludeChange: (checked: boolean) => void;
}

export const CustomFieldFilterDrawer = ({
	definition,
	values,
	excluded,
	allowExclude,
	format,
	onReplace,
	onExcludeChange,
}: CustomFieldFilterDrawerProps): JSX.Element => {
	const intl = useIntl();
	const say: FormatMessage = (descriptor, messageValues) => intl.formatMessage(descriptor, messageValues) as string;
	const cy = `cf-filter-${definition.key}`;
	const notWord = say({id: NOT_MESSAGE_ID});
	// A click on the row's blank space presses the switch's own checkbox, so there is one change path. A click
	// on the switch itself already toggles it and is left alone, which keeps it from firing twice.
	const toggleExcludeFromRow = (event: React.MouseEvent<HTMLElement>) => {
		if ((event.target as HTMLElement).closest('input, label')) return;
		event.currentTarget.querySelector<HTMLInputElement>('input[type="checkbox"]')?.click();
	};
	return (
		<Drawer data-cy={`cf-filter-drawer-${definition.key}`}>
			{allowExclude && (
				<ExcludeRow data-cy={`${cy}-exclude`} onClick={toggleExcludeFromRow}>
					{/* The line keeps its own on/off state, so it starts again whenever the stored state moves. */}
					<SwitchLine
						key={String(excluded)}
						name={say({id: 'common.filter.exclude'})}
						onToggle={(checked: boolean) => onExcludeChange(checked)}
						initToggleState={excluded}
					/>
				</ExcludeRow>
			)}
			<div data-cy={`${cy}-applied`}>
				<Heading>
					{say({id: 'common.applied'})}{' '}
					<Muted>{say({id: 'filters.custom_field.count'}, {count: values.length})}</Muted>
				</Heading>
				<AppliedRows>
					{values.map((value, index) => {
						const label = customFieldValueLabel(definition, value, say, format);
						return (
							<AppliedRow key={`${index}-${label}`} data-cy={`${cy}-applied-row`}>
								<span>
									{excluded && (
										<>
											<NotWord>{notWord}</NotWord>{' '}
										</>
									)}
									{label}
								</span>
								<RemoveButton
									type="button"
									data-cy={`${cy}-remove`}
									aria-label={say({id: 'filters.custom_field.remove_value'}, {label})}
									title={say({id: 'filters.custom_field.remove_value'}, {label})}
									onClick={() => onReplace(values.filter((_, other) => other !== index))}
								>
									<CrossIconOld />
								</RemoveButton>
							</AppliedRow>
						);
					})}
				</AppliedRows>
			</div>
			<FieldHeading>
				<span>{definition.displayName}</span> <Muted>{customFieldTypeLabel(definition, say)}</Muted>
			</FieldHeading>
			{definition.kind === 'DROPDOWN' ? (
				<CustomFieldDropdownOptions definition={definition} applied={values} onApply={onReplace} />
			) : (
				<CustomFieldConditionFilter
					definition={definition}
					applied={values as ValueCondition[]}
					decimalSeparator={format.decimalSeparator}
					locale={format.locale}
					onApply={onReplace}
				/>
			)}
		</Drawer>
	);
};
